import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Checkbox,
  Flex,
  Form,
  Modal,
  Typography
} from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsOrganizationsEnvironmentBuildCreateMutation,
  organizationsOrganizationsEnvironmentClearCreateMutation,
  SetupJobStatusEnum,
  type Organization,
  type SetupJob
} from 'src/api/main'
import { ConfirmActionModal } from 'src/components'
import {
  IndustrySelect,
  SetupProgressStepperModal
} from 'src/features/organizations/components'
import { useShowApiErrors } from 'src/utils'

const { Text } = Typography

type DemoDataSectionProps = {
  organization: Organization
}

type RecreateFormValues = {
  preset?: number
  install_missing_applications?: boolean
}

export const DemoDataSection = ({ organization }: DemoDataSectionProps) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()

  const [clearOpen, setClearOpen] = useState(false)
  const [recreateOpen, setRecreateOpen] = useState(false)
  const [progressJobId, setProgressJobId] = useState<number>()
  const [progressOpen, setProgressOpen] = useState(false)

  const [recreateForm] = Form.useForm<RecreateFormValues>()

  const onJobStarted = (job: SetupJob | undefined, nothingToRun: string) => {
    // A job with no steps for the installed apps comes back already finished.
    if (job?.status === SetupJobStatusEnum.FINISHED) {
      message.info(nothingToRun)
      return
    }
    message.success(t('workspace.settings.demoDataBuildStarted'))
    if (job?.id) {
      setProgressJobId(job.id)
      setProgressOpen(true)
    }
  }

  const { mutate: recreateEnvironment, isPending: isRecreating } = useMutation({
    ...organizationsOrganizationsEnvironmentBuildCreateMutation(),
    onSuccess: (job) =>
      onJobStarted(job, t('workspace.settings.demoDataNothingToCreate')),
    onError: (error) => showApiErrors(error)
  })

  const { mutate: runClear, isPending: isClearing } = useMutation({
    ...organizationsOrganizationsEnvironmentClearCreateMutation(),
    onSuccess: (job) =>
      onJobStarted(job, t('workspace.settings.demoDataNothingToClear')),
    onError: (error) => showApiErrors(error)
  })

  const handleClear = () => {
    setClearOpen(false)
    runClear({ path: { id: organization.id } })
  }

  const handleRecreate = async () => {
    const values = await recreateForm.validateFields()
    if (values.preset == null) return
    setRecreateOpen(false)
    recreateForm.resetFields()
    recreateEnvironment({
      path: { id: organization.id },
      body: {
        preset: values.preset,
        ...(values.install_missing_applications
          ? { install_missing_applications: true }
          : {})
      } as { preset: number | null }
    })
  }

  const handleCloseRecreate = () => {
    setRecreateOpen(false)
    recreateForm.resetFields()
  }

  return (
    <>
      <Card variant="outlined" title={t('workspace.settings.demoDataSection')}>
        <Flex vertical gap={16}>
          <Text type="secondary">
            {t('workspace.settings.demoDataDescription')}
          </Text>

          <Flex justify="space-between" align="center" gap={16} wrap="wrap">
            <Flex vertical gap={4} style={{ flex: 1, minWidth: 200 }}>
              <Text strong>
                {t('workspace.settings.recreateDemoDataTitle')}
              </Text>
              <Text type="secondary">
                {t('workspace.settings.recreateDemoDataDescription')}
              </Text>
            </Flex>
            <Button
              type="primary"
              onClick={() => setRecreateOpen(true)}
              loading={isRecreating}
              disabled={isClearing}
            >
              {t('workspace.settings.recreateDemoData')}
            </Button>
          </Flex>

          <Flex justify="space-between" align="center" gap={16} wrap="wrap">
            <Flex vertical gap={4} style={{ flex: 1, minWidth: 200 }}>
              <Text strong>{t('workspace.settings.clearDemoDataTitle')}</Text>
              <Text type="secondary">
                {t('workspace.settings.clearDemoDataDescription')}
              </Text>
            </Flex>
            <Button
              danger
              onClick={() => setClearOpen(true)}
              loading={isClearing}
              disabled={isRecreating}
            >
              {t('workspace.settings.clearDemoData')}
            </Button>
          </Flex>
        </Flex>
      </Card>

      <Modal
        open={recreateOpen}
        title={t('workspace.settings.recreateDemoDataConfirmTitle')}
        okText={t('workspace.settings.recreateDemoData')}
        okButtonProps={{ loading: isRecreating }}
        onOk={() => void handleRecreate()}
        onCancel={handleCloseRecreate}
        destroyOnHidden
      >
        <Flex vertical gap={16} style={{ paddingTop: 8 }}>
          <Text>
            {t('workspace.settings.recreateDemoDataConfirmDescription')}
          </Text>
          <Form
            form={recreateForm}
            layout="vertical"
            requiredMark={false}
            initialValues={{ install_missing_applications: false }}
          >
            <Form.Item
              label={t('workspace.settings.demoDataIndustryLabel')}
              name="preset"
              rules={[
                {
                  required: true,
                  message: t('workspace.settings.demoDataIndustryRequired')
                }
              ]}
            >
              <IndustrySelect />
            </Form.Item>
            <Form.Item
              name="install_missing_applications"
              valuePropName="checked"
              style={{ marginBottom: 4 }}
            >
              <Checkbox>
                {t('workspace.settings.demoDataInstallMissingAppsLabel')}
              </Checkbox>
            </Form.Item>
            <Text type="secondary">
              {t('workspace.settings.demoDataInstallMissingAppsHint')}
            </Text>
          </Form>
        </Flex>
      </Modal>

      <ConfirmActionModal
        open={clearOpen}
        title={t('workspace.settings.clearDemoDataConfirmTitle')}
        description={t('workspace.settings.clearDemoDataConfirmDescription')}
        confirmText="clear"
        confirmPlaceholder={t('workspace.settings.typeToConfirm', {
          name: 'clear'
        })}
        warningMessage={t('workspace.settings.clearDemoDataWarning')}
        confirmButtonText={t('workspace.settings.clearDemoData')}
        loading={isClearing}
        onConfirm={handleClear}
        onCancel={() => setClearOpen(false)}
      />

      <SetupProgressStepperModal
        key={progressJobId ?? 'idle'}
        open={progressOpen}
        onCancel={() => setProgressOpen(false)}
        parentId={progressJobId}
        organization={organization}
      />
    </>
  )
}
